import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getPriorityColor,
  getTimeAgo,
} from "../../../../store/lite/notificationsStore";
import { useReportsStore, STALE_REPORT_MS } from "../../../../store/reports/reportsStore";
import {
  useLiteNotifications,
  markLiteNotificationRead,
  markAllLiteNotificationsRead,
} from "../../../../features/notifications/liteQueries";
import { queryClient } from "../../../../lib/queryClient";
import { ChevronIcon } from "./icons";
import {
  trackNotificationsToggle,
  trackNotificationsMarkAllRead,
  trackNotificationsScrollLoadMore,
  trackNotificationClick,
  trackNotificationsEmptyState,
} from "../sidebarAnalytics";

interface NotificationsSectionProps {
  isDarkMode: boolean;
  // Dentro de la pestaña «Plataforma» de la campana del header: sin el
  // encabezado plegable (la campana ya lo es) y siempre abierta.
  bare?: boolean;
}

function NotificationsEmptyState({ textSecondary }: { textSecondary: string }) {
  const { t } = useTranslation("common");

  useEffect(() => {
    trackNotificationsEmptyState();
  }, []);

  return (
    <div className="p-4 text-center">
      <i className={`ri-notification-off-line text-2xl ${textSecondary}`}></i>
      <p className={`text-xs ${textSecondary} mt-2`}>
        {t("notifications.empty")}
      </p>
    </div>
  );
}

export default function NotificationsSection({
  isDarkMode,
  bare = false,
}: NotificationsSectionProps) {
  const { t } = useTranslation("common");

  const {
    items: notifications,
    unreadCount,
    isLoading: notificationsLoading,
    isFetchingNextPage: notificationsLoadingMore,
    hasMore: hasMoreNotifications,
    fetchNextPage,
  } = useLiteNotifications();

  const {
    reports,
    loadingReports,
    getReportsOfUser,
    hasPendingReports,
    startPolling,
    stopPolling,
  } = useReportsStore();

  const reportTypes: Record<string, string> = {
    historic: t("reportTypes.historic"),
    speed: t("reportTypes.speed"),
    driving: t("reportTypes.driving"),
    temperature: t("reportTypes.temperature"),
    mileage: t("reportTypes.mileage"),
    trailer: t("reportTypes.trailer"),
    routes: t("reportTypes.routes"),
    odometer: t("reportTypes.odometer"),
    stops: t("reportTypes.stops"),
  };

  // Filtrar reportes pendientes/en proceso para mostrar en notificaciones.
  // Ignoramos los stale (>15 min sin heartbeat) — el watchdog del backend los
  // marcará como failed pronto, pero este filtro evita que aparezcan alertas
  // fantasma si el watchdog se atrasa o si el deploy interrumpió varios jobs.
  const pendingReports = useMemo(() => {
    const now = Date.now();
    return reports.filter(r => {
      if (r.status !== "pending" && r.status !== "processing") return false;
      const ts = r.updatedAt ?? r.createdAt;
      const updatedMs = ts ? ts._seconds * 1000 : 0;
      if (updatedMs === 0) return true;
      return now - updatedMs < STALE_REPORT_MS;
    });
  }, [reports]);

  // Contador total incluye notificaciones no leídas + reportes pendientes
  const totalUnreadCount = unreadCount + pendingReports.length;

  // Expand by default only if there are unread notifications or pending reports
  const [isExpanded, setIsExpanded] = useState(bare || totalUnreadCount > 0);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Update expanded state when unreadCount changes after initial load
  useEffect(() => {
    if (!notificationsLoading && !loadingReports && !hasInitialized) {
      setIsExpanded(totalUnreadCount > 0);
      setHasInitialized(true);
    }
  }, [totalUnreadCount, notificationsLoading, loadingReports, hasInitialized]);

  // Al abrir el sidebar, refrescar notificaciones por si llegó algo mientras
  // estuvo cerrado. Si el usuario no habilitó las push, un GET es la única
  // forma de verlas.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["liteNotifications"] });
  }, []);

  // Cargar reportes e iniciar polling si hay pendientes
  useEffect(() => {
    const loadReports = async () => {
      await getReportsOfUser();
      if (hasPendingReports()) {
        startPolling();
      }
    };
    loadReports();
    return () => stopPolling();
  }, []);

  // Manejar polling cuando cambian los reportes
  useEffect(() => {
    if (hasPendingReports()) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [reports]);

  // Cuando un reporte pendiente termina (deja de estar "obteniendo reporte"),
  // invalidar notificaciones para que aparezca "Reporte listo" sin depender de FCM.
  const prevHasPendingRef = useRef(hasPendingReports());
  useEffect(() => {
    const hadPending = prevHasPendingRef.current;
    const hasPending = hasPendingReports();
    prevHasPendingRef.current = hasPending;
    if (hadPending && !hasPending) {
      queryClient.invalidateQueries({ queryKey: ["liteNotifications"] });
    }
  }, [reports]);

  const notificationsListRef = useRef<HTMLDivElement>(null);

  const themeClasses = {
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-500",
    cardBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-card" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const handleNotificationsScroll = () => {
    const container = notificationsListRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasMoreNotifications && !notificationsLoadingMore) {
        trackNotificationsScrollLoadMore();
        fetchNextPage();
      }
    }
  };

  return (
    <div className={bare ? "" : "mb-4"}>
      {bare ? (
        unreadCount > 0 && (
          <div className="flex justify-end px-1 pb-2">
            <button
              type="button"
              onClick={() => {
                trackNotificationsMarkAllRead(unreadCount);
                markAllLiteNotificationsRead();
              }}
              className="text-xs text-primary-500 hover:underline"
            >
              {t("notifications.markAll")}
            </button>
          </div>
        )
      ) : (
      <button
        onClick={() => {
          const nextState = !isExpanded;
          setIsExpanded(nextState);
          trackNotificationsToggle(nextState ? "expand" : "collapse", unreadCount);
        }}
        className={`w-full flex items-center justify-between gap-3 mb-3 ${isDarkMode ? "hover:bg-dark-surface" : "hover:bg-gray-100"
          } rounded-lg p-2 transition-colors`}
      >
        <div className="text-left flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="relative mr-1">
              <i
                className={`ri-notification-3-line text-lg ${isDarkMode ? "text-primary-500" : "text-primary-400"}`}
              ></i>
              {totalUnreadCount > 0 && (
                <span className="py-0.5 text-xs font-medium bg-red-500 text-white rounded-full min-w-[20px] text-center absolute -top-1 -right-3">
                  {totalUnreadCount > 9 ? "9+" : totalUnreadCount}
                </span>
              )}
            </div>
            <h3 className={`font-semibold text-sm ${themeClasses.text}`}>
              {t("notifications.sectionTitle")}
            </h3>

          </div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("notifications.sectionSubtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                trackNotificationsMarkAllRead(unreadCount);
                markAllLiteNotificationsRead();
              }}
              className="text-xs text-primary-500 hover:text-primary-400 font-medium transition-colors cursor-pointer"
            >
              {t("notifications.markAll")}
            </span>
          )}
          <ChevronIcon
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            isExpanded={isExpanded}
          />
        </div>
      </button>
      )}

      {isExpanded && (
        <div
          className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}
        >
          <div
            ref={notificationsListRef}
            onScroll={handleNotificationsScroll}
            className="max-h-64 overflow-y-auto"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: isDarkMode ? "#4A4A4A transparent" : "#d1d5db transparent",
            }}
          >
            {notificationsLoading ? (
              <div className="p-4 text-center">
                <i className="ri-loader-4-line animate-spin text-primary-500 text-xl"></i>
                <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                  {t("notifications.loading")}
                </p>
              </div>
            ) : notifications.length === 0 && pendingReports.length === 0 ? (
              <NotificationsEmptyState textSecondary={themeClasses.textSecondary} />
            ) : (
              <>
                {/* Reportes pendientes/en proceso primero */}
                {pendingReports.map((report, index) => {
                  const isPending = report.status === "pending";
                  const isProcessing = report.status === "processing";

                  return (
                    <div
                      key={`report-${report.reportId}`}
                      className={`p-3 ${themeClasses.buttonHover} cursor-default ${index < pendingReports.length - 1 || notifications.length > 0
                        ? `border-b ${themeClasses.cardBorder}`
                        : ""
                        } transition-colors ${isDarkMode ? "bg-dark-card/30" : "bg-blue-50/30"
                        }`}
                    >
                      <div className="flex items-start space-x-2">
                        <div className="flex-shrink-0 mt-0.5">
                          {isPending && (
                            <div className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center">
                              <i className="ri-time-line text-yellow-500 text-xs"></i>
                            </div>
                          )}
                          {isProcessing && (
                            <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <i className="ri-loader-4-line text-blue-500 text-xs animate-spin"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                              {t("notifications.report", {
                                type:
                                  reportTypes[report.typeReport as keyof typeof reportTypes] ||
                                  report.typeReport,
                              })}
                            </p>
                            {isPending && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 ml-2 flex-shrink-0">
                                {t("notifications.queued")}
                              </span>
                            )}
                            {isProcessing && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 ml-2 flex-shrink-0">
                                {t("notifications.generating")}
                              </span>
                            )}
                          </div>
                          <p className={`text-xs ${themeClasses.textSecondary} mt-1 truncate`}>
                            {report.fileName || t("notifications.processing")}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Notificaciones regulares */}
                {notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    onClick={async () => {
                      trackNotificationClick(
                        notification.id,
                        notification.title,
                        notification.priority,
                        notification.isRead
                      );
                      if (!notification.isRead) {
                        await markLiteNotificationRead(notification.id);
                      }
                      const target = notification.url || "/admin/notifications";
                      const isExternal = target.startsWith("http") && !target.startsWith(window.location.origin);
                      if (isExternal) {
                        window.open(target, "_blank", "noopener,noreferrer");
                      } else {
                        const path = target.startsWith("http")
                          ? new URL(target).pathname
                          : target;
                        window.location.href = path;
                      }
                    }}
                    className={`p-3 ${themeClasses.buttonHover} cursor-pointer ${index < notifications.length - 1
                      ? `border-b ${themeClasses.cardBorder}`
                      : ""
                      } transition-colors ${!notification.isRead
                        ? isDarkMode
                          ? "bg-dark-card/50"
                          : "bg-blue-50/50"
                        : "opacity-60"
                      }`}
                  >
                    <div className="flex items-start space-x-2">
                      {/* Indicador de no leído */}
                      <div className="flex-shrink-0 mt-0.5">
                        {!notification.isRead ? (
                          <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-500/20 flex items-center justify-center">
                            <i className="ri-check-line text-gray-500 text-xs"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p
                            className={`text-sm font-medium ${themeClasses.text} truncate ${notification.isRead ? "opacity-70" : ""
                              }`}
                          >
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-500 ml-2 flex-shrink-0">
                              {t("notifications.new")}
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-xs ${themeClasses.textSecondary} mt-1 line-clamp-2`}
                        >
                          {notification.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`w-1.5 h-1.5 ${getPriorityColor(notification.priority)} rounded-full`}></span>
                          <p className={`text-xs ${themeClasses.textSecondary}`}>
                            {getTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {notificationsLoadingMore && (
                  <div
                    className={`p-3 text-center border-t border-dashed ${themeClasses.cardBorder}`}
                  >
                    <i className="ri-loader-4-line animate-spin text-primary-500 text-lg"></i>
                    <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                      {t("notifications.loadingMore")}
                    </p>
                  </div>
                )}
                {!hasMoreNotifications &&
                  notifications.length > 0 &&
                  !notificationsLoadingMore && (
                    <div className="p-2 text-center">
                      <p className={`text-xs ${themeClasses.textSecondary}`}>
                        {t("notifications.noMore")}
                      </p>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
